import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUserCircle, FaPlus, FaBuilding, FaTrash, FaEdit, FaBars, FaTimes, FaKey, FaShieldAlt } from 'react-icons/fa';
import { ref, get, set, update } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import CompanySwitcher from './CompanySwitcher';
import DivisionCard from './DivisionCard';
import { normalizeCompanyKey, firebaseCompanyKey } from '../../Utils/companyUtils';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from 'react-modal';

const DEFAULT_COMPANIES = [];

const DEFAULT_DIVISIONS = [];

Modal.setAppElement('#root');

const Dashboard = () => {
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState(() => {
    return localStorage.getItem('selectedCompany') || '';
  });
  const [divisions, setDivisions] = useState([]);
  const [divisionStats, setDivisionStats] = useState({});
  const [refreshCompanies, setRefreshCompanies] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Modals state
  const [addCompanyModalOpen, setAddCompanyModalOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  const [deleteCompanyModalOpen, setDeleteCompanyModalOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');

  const [addDivisionModalOpen, setAddDivisionModalOpen] = useState(false);
  const [newDivisionName, setNewDivisionName] = useState('');

  const [editDivisionModalOpen, setEditDivisionModalOpen] = useState(false);
  const [editDivisionOldName, setEditDivisionOldName] = useState('');
  const [editDivisionNewName, setEditDivisionNewName] = useState('');

  const [deleteDivisionModalOpen, setDeleteDivisionModalOpen] = useState(false);
  const [divisionToDelete, setDivisionToDelete] = useState('');

  useEffect(() => {
    if (!selectedCompany) return;
    localStorage.setItem('selectedCompany', selectedCompany);
    
    // Reset state immediately when company changes to avoid stale data
    setDivisions([]);
    setDivisionStats({});
    setIsLoading(true);

    const fetchCompanyData = async () => {
      try {
        const companyKey = firebaseCompanyKey(selectedCompany);
        const companyRef = ref(realtimeDb, `companies/${companyKey}`);
        const snap = await get(companyRef);
        const companyData = snap.val();
        if (snap.exists() && companyData.divisions) {
          const divs = companyData.divisions;
          const divisionsList = Array.isArray(divs) ? divs : Object.values(divs);
          setDivisions(divisionsList);

          const newStats = {};
          
          // Fetch stats for all divisions in parallel
          await Promise.all(divisionsList.map(async (div) => {
            // Use division name as-is — Firebase stores it exactly as entered (e.g. "VITA", "R-1")
            // Do NOT normalize division key, that converts "VITA" → "Vita" causing path mismatch
            newStats[div] = {};
            
            // Fetch register and entryPass data from correct Firebase paths
            const [registerSnap, entryPassSnap] = await Promise.all([
              get(ref(realtimeDb, `companies/${companyKey}/${div}/register`)),
              get(ref(realtimeDb, `companies/${companyKey}/${div}/entryPass`)),
            ]);
            
            const registerData = registerSnap.exists() ? registerSnap.val() : {};
            const entryPassData = entryPassSnap.exists() ? entryPassSnap.val() : {};
            const entries = Object.values({ ...entryPassData, ...registerData });
            
            entries.forEach(row => {
               const kva = row.kva;
               if (!kva) return; // Skip if no KVA is specified
               
               if (!newStats[div][kva]) {
                 newStats[div][kva] = { completed: 0, pending: 0 };
               }
               
               if (row.completed) {
                 newStats[div][kva].completed += 1;
               } else {
                 newStats[div][kva].pending += 1;
               }
            });
          }));
          
          setDivisionStats(newStats);
        } else {
          setDivisions([]);
          setDivisionStats({});
        }
      } catch (error) {
        toast.error('Failed to load company data');
        console.error("Error fetching company data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyData();
  }, [selectedCompany]);

  const handleCompanyChange = (newCompany) => {
    setSelectedCompany(newCompany);
    localStorage.setItem('selectedCompany', newCompany);
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) {
      toast.warning('Please enter a company name');
      return;
    }

    const displayName = normalizeCompanyKey(newCompanyName.trim());
    const companyKey = firebaseCompanyKey(displayName);
    const companyRef = ref(realtimeDb, `companies/${companyKey}`);

    try {
      setIsLoading(true);

      // Check if company already exists
      const existingSnap = await get(companyRef);
      if (existingSnap.exists()) {
        toast.warning(`Company "${displayName}" already exists`);
        setIsLoading(false);
        return;
      }

      await set(companyRef, {
        name: displayName,
        createdAt: new Date().toISOString(),
      });

      toast.success(`Company "${displayName}" added successfully`);
      setAddCompanyModalOpen(false);
      setNewCompanyName('');
      setRefreshCompanies((prev) => !prev);
    } catch (error) {
      toast.error(`Failed to add company: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (DEFAULT_COMPANIES.includes(selectedCompany)) {
      toast.warning('Default companies cannot be deleted');
      setDeleteCompanyModalOpen(false);
      return;
    }

    if (!adminPassword) {
      toast.warning('Admin password is required');
      return;
    }

    try {
      setIsLoading(true);
      const adminSnap = await get(ref(realtimeDb, 'power/admin'));
      if (!adminSnap.exists() || adminSnap.val().password !== adminPassword) {
        toast.error('Incorrect admin password');
        setIsLoading(false);
        return;
      }

      const snap = await get(ref(realtimeDb, 'companies'));
      if (snap.exists()) {
        const data = snap.val();
        const updates = {};
        Object.keys(data).forEach(key => {
          if (normalizeCompanyKey(key) === selectedCompany) {
            updates[key] = null;
          }
        });
        await update(ref(realtimeDb, 'companies'), updates);
      } else {
        const companyKey = firebaseCompanyKey(selectedCompany);
        await set(ref(realtimeDb, `companies/${companyKey}`), null);
      }
      
      // Delete associated data (entries and gatepasses)
      const entriesSnap = await get(ref(realtimeDb, 'power/entries'));
      if (entriesSnap.exists()) {
        const entriesUpdates = {};
        entriesSnap.forEach(child => {
          if (normalizeCompanyKey(child.val().companyName) === selectedCompany) {
            entriesUpdates[child.key] = null;
          }
        });
        if (Object.keys(entriesUpdates).length > 0) await update(ref(realtimeDb, 'power/entries'), entriesUpdates);
      }

      const gpSnap = await get(ref(realtimeDb, 'power/gatepasses'));
      if (gpSnap.exists()) {
        const gpUpdates = {};
        gpSnap.forEach(child => {
          if (normalizeCompanyKey(child.val().companyName) === selectedCompany) {
            gpUpdates[child.key] = null;
          }
        });
        if (Object.keys(gpUpdates).length > 0) await update(ref(realtimeDb, 'power/gatepasses'), gpUpdates);
      }

      toast.success(`Company "${selectedCompany}" and all its data deleted successfully`);

      setDeleteCompanyModalOpen(false);
      setAdminPassword('');
      localStorage.removeItem('selectedCompany');
      setSelectedCompany('');
      setRefreshCompanies((prev) => !prev);
    } catch (error) {
      toast.error(`Failed to delete company: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDivisionClick = (division) => {
    navigate(`/admin/division/${division}`, {
      state: { company: selectedCompany },
    });
  };

  const handleAddDivision = async () => {
    const trimmedDivisionName = newDivisionName.trim();

    if (!trimmedDivisionName) {
      toast.warning('Please enter a division name');
      return;
    }

    if (divisions.includes(trimmedDivisionName)) {
      toast.warning('Division with this name already exists');
      return;
    }

    try {
      setIsLoading(true);
      const updated = [...divisions, trimmedDivisionName];
      setDivisions(updated);
      const divisionRef = ref(realtimeDb, `companies/${firebaseCompanyKey(selectedCompany)}/divisions`);
      await set(divisionRef, updated);
      toast.success(`Division "${trimmedDivisionName}" added successfully`);
      setAddDivisionModalOpen(false);
      setNewDivisionName('');
    } catch (error) {
      toast.error(`Failed to add division: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDivision = async () => {
    if (DEFAULT_DIVISIONS.includes(divisionToDelete)) {
      toast.warning('Default divisions cannot be deleted');
      setDeleteDivisionModalOpen(false);
      return;
    }

    if (!adminPassword) {
      toast.warning('Admin password is required');
      return;
    }

    try {
      setIsLoading(true);
      const adminSnap = await get(ref(realtimeDb, 'power/admin'));
      if (!adminSnap.exists() || adminSnap.val().password !== adminPassword) {
        toast.error('Incorrect admin password');
        setIsLoading(false);
        return;
      }

      const updated = divisions.filter((d) => d !== divisionToDelete);
      setDivisions(updated);
      const divisionRef = ref(realtimeDb, `companies/${firebaseCompanyKey(selectedCompany)}/divisions`);
      await set(divisionRef, updated.length > 0 ? updated : null);

      // Delete associated data (entries and gatepasses)
      const entriesSnap = await get(ref(realtimeDb, 'power/entries'));
      if (entriesSnap.exists()) {
        const entriesUpdates = {};
        entriesSnap.forEach(child => {
          if (normalizeCompanyKey(child.val().companyName) === selectedCompany && child.val().division === divisionToDelete) {
            entriesUpdates[child.key] = null;
          }
        });
        if (Object.keys(entriesUpdates).length > 0) await update(ref(realtimeDb, 'power/entries'), entriesUpdates);
      }

      const gpSnap = await get(ref(realtimeDb, 'power/gatepasses'));
      if (gpSnap.exists()) {
        const gpUpdates = {};
        gpSnap.forEach(child => {
          if (normalizeCompanyKey(child.val().companyName) === selectedCompany && child.val().division === divisionToDelete) {
            gpUpdates[child.key] = null;
          }
        });
        if (Object.keys(gpUpdates).length > 0) await update(ref(realtimeDb, 'power/gatepasses'), gpUpdates);
      }

      toast.success(`Division "${divisionToDelete}" and its data deleted successfully`);
      setDeleteDivisionModalOpen(false);
      setAdminPassword('');
      setDivisionToDelete('');
    } catch (error) {
      toast.error(`Failed to delete division: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditDivision = async () => {
    const newTrimmed = editDivisionNewName.trim();

    if (!newTrimmed || newTrimmed === editDivisionOldName) {
      setEditDivisionModalOpen(false);
      return;
    }

    if (divisions.includes(newTrimmed)) {
      toast.warning('Division with this name already exists');
      return;
    }

    try {
      setIsLoading(true);
      const updated = divisions.map((d) => (d === editDivisionOldName ? newTrimmed : d));
      setDivisions(updated);
      const divisionRef = ref(realtimeDb, `companies/${firebaseCompanyKey(selectedCompany)}/divisions`);
      await set(divisionRef, updated);
      toast.success(`Division renamed to "${newTrimmed}" successfully`);
      setEditDivisionModalOpen(false);
      setEditDivisionOldName('');
      setEditDivisionNewName('');
    } catch (error) {
      toast.error(`Failed to rename division: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditDivisionModal = (oldName) => {
    setEditDivisionOldName(oldName);
    setEditDivisionNewName(oldName);
    setEditDivisionModalOpen(true);
  };

  const openDeleteDivisionModal = (division) => {
    setDivisionToDelete(division);
    setAdminPassword('');
    setDeleteDivisionModalOpen(true);
  };

  const modalStyle = {
    content: {
      top: '50%', left: '50%', right: 'auto', bottom: 'auto',
      marginRight: '-50%', transform: 'translate(-50%, -50%)',
      border: 'none', background: 'transparent', padding: 0,
      width: '100%', maxWidth: '450px'
    },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', zIndex: 1000 }
  };

  // Calculate overall company stats KVA-wise and total stats
  const companyKvaStats = {};
  let hasStats = false;
  let overallCompleted = 0;
  let overallPending = 0;
  
  Object.values(divisionStats).forEach(divStats => {
    Object.entries(divStats).forEach(([kva, stat]) => {
      if (!companyKvaStats[kva]) {
        companyKvaStats[kva] = { completed: 0, pending: 0 };
      }
      companyKvaStats[kva].completed += stat.completed;
      companyKvaStats[kva].pending += stat.pending;
      overallCompleted += stat.completed;
      overallPending += stat.pending;
      if (stat.completed > 0 || stat.pending > 0) hasStats = true;
    });
  });
  let overallTotal = overallCompleted + overallPending;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Sidebar */}
      <aside className={`w-72 bg-white text-gray-800 border-r border-gray-200 flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 lg:static lg:translate-x-0 shadow-2xl shrink-0`}>
        <div className="p-6 flex items-center justify-between border-b border-gray-100">
          <h1 className="text-xl font-bold flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-lg">
              <FaShieldAlt className="text-white" />
            </div>
            Admin Portal
          </h1>
          <button className="lg:hidden text-gray-400 hover:text-gray-800" onClick={() => setSidebarOpen(false)}>
            <FaTimes className="text-xl" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-100">
          <label className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3 block">Select Company</label>
          <div className="rounded-xl">
            <CompanySwitcher
              selectedCompany={selectedCompany}
              onChange={handleCompanyChange}
              refreshFlag={refreshCompanies}
              isLoading={isLoading}
            />
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => setAddCompanyModalOpen(true)}
            className="w-full text-left px-4 py-3.5 rounded-xl flex items-center text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <FaPlus className="mr-3 text-lg" />
            Add Company
          </button>

          {!DEFAULT_COMPANIES.includes(selectedCompany) && selectedCompany && (
            <button
              onClick={() => {
                setAdminPassword('');
                setDeleteCompanyModalOpen(true);
              }}
              className="w-full text-left px-4 py-3.5 rounded-xl flex items-center text-red-600 hover:bg-red-50 transition-colors"
            >
              <FaTrash className="mr-3 text-lg" />
              Delete Company
            </button>
          )}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <button
            onClick={() => navigate('/admin/profile')}
            className="w-full flex items-center justify-center gap-2 bg-indigo-50 text-indigo-600 px-4 py-3 rounded-xl hover:bg-indigo-100 hover:text-indigo-700 transition-colors font-medium mb-2"
          >
            <FaUserCircle />
            Admin Settings
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Top Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 p-4 flex items-center justify-between z-10 shrink-0 sticky top-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-indigo-600 p-2 bg-gray-100 rounded-lg transition-colors"
            >
              <FaBars className="text-xl" />
            </button>
            <h2 className="text-xl font-bold text-gray-800 ml-2 lg:ml-0 flex items-center gap-2">
              Dashboard
            </h2>
          </div>
        </header>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-gray-50/50">
          <div className="max-w-6xl mx-auto">
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            ) : selectedCompany ? (
              <div className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-100">
                <div className="p-6 md:p-8 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex items-center gap-5">
                      <div className="p-4 bg-white rounded-2xl shadow-sm border border-indigo-100">
                        <FaBuilding className="text-indigo-600 text-3xl" />
                      </div>
                      <div className="flex flex-col">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight">
                          {normalizeCompanyKey(selectedCompany)}
                        </h2>
                        <p className="text-md text-gray-600 font-medium mt-1">
                          {divisions.length} {divisions.length === 1 ? 'Division' : 'Divisions'}
                        </p>
                        
                        {hasStats && (
                          <div className="flex items-center bg-white rounded-xl shadow-sm border border-gray-200 py-2.5 px-5 gap-6 mt-3 self-start">
                            <div className="flex flex-col items-center justify-center min-w-[70px]">
                              <span className="text-[11px] text-gray-500 font-bold uppercase tracking-wider mb-0.5">Total Jobs</span>
                              <span className="text-xl font-black text-gray-800">{overallTotal}</span>
                            </div>
                            <div className="w-px h-10 bg-gray-200"></div>
                            <div className="flex flex-col items-center justify-center min-w-[70px]">
                              <span className="text-[11px] text-emerald-600 font-bold uppercase tracking-wider mb-0.5">Completed</span>
                              <span className="text-xl font-black text-emerald-600">{overallCompleted}</span>
                            </div>
                            <div className="w-px h-10 bg-gray-200"></div>
                            <div className="flex flex-col items-center justify-center min-w-[70px]">
                              <span className="text-[11px] text-rose-600 font-bold uppercase tracking-wider mb-0.5">Pending</span>
                              <span className="text-xl font-black text-rose-600">{overallPending}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Overall Company KVA Stats */}
                    {hasStats && (
                      <div className="flex flex-wrap gap-3 self-start lg:self-center shrink-0 lg:max-w-xl xl:max-w-3xl lg:justify-end">
                        {Object.entries(companyKvaStats).sort((a, b) => Number(a[0]) - Number(b[0])).map(([kva, stat]) => (
                          <div key={kva} className="flex bg-white rounded-xl shadow-sm border border-indigo-100 py-2 px-3 gap-3">
                            <div className="flex flex-col items-center justify-center min-w-[45px]">
                              <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-0.5">KVA</span>
                              <span className="text-sm font-black text-gray-800">{kva}</span>
                            </div>
                            <div className="w-px bg-gray-100"></div>
                            <div className="flex flex-col items-center justify-center min-w-[35px]">
                              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5" title="Completed">C</span>
                              <span className="text-sm font-black text-emerald-600">{stat.completed}</span>
                            </div>
                            <div className="w-px bg-gray-100"></div>
                            <div className="flex flex-col items-center justify-center min-w-[35px]">
                              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5" title="Pending">P</span>
                              <span className="text-sm font-black text-rose-600">{stat.pending}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 md:p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {divisions.map((division) => (
                      <DivisionCard
                        key={division}
                        name={division}
                        stats={divisionStats[division] || {}}
                        onClick={() => handleDivisionClick(division)}
                        onDelete={DEFAULT_DIVISIONS.includes(division) ? null : () => openDeleteDivisionModal(division)}
                        onEdit={DEFAULT_DIVISIONS.includes(division) ? null : () => openEditDivisionModal(division)}
                      />
                    ))}

                    <button
                      onClick={() => { setNewDivisionName(''); setAddDivisionModalOpen(true); }}
                      disabled={!selectedCompany || isLoading}
                      className="flex flex-col items-center justify-center h-full min-h-[160px] border-2 border-dashed border-gray-300 hover:border-indigo-400 rounded-2xl p-6 transition-all hover:bg-indigo-50/50 group disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                    >
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-indigo-50 group-hover:bg-indigo-100 transition-colors mb-3">
                        <FaPlus className="text-indigo-600 text-xl" />
                      </div>
                      <span className="text-indigo-700 font-semibold text-md">Add Division</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-sm p-16 text-center border border-gray-100 mt-10">
                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-50 rounded-full flex items-center justify-center mb-6 border border-gray-200 shadow-inner">
                  <FaBuilding className="text-gray-400 text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3 tracking-tight">No Company Selected</h3>
                <p className="text-gray-500 max-w-md mx-auto text-lg">
                  Please select a company from the sidebar to view and manage its divisions.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      
      {/* Add Company Modal */}
      <Modal isOpen={addCompanyModalOpen} onRequestClose={() => setAddCompanyModalOpen(false)} style={modalStyle}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3">
            <div className="bg-indigo-100 p-2 rounded-full"><FaBuilding className="text-indigo-600" /></div>
            Add New Company
          </h2>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter company name"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setAddCompanyModalOpen(false)} className="px-5 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200">Cancel</button>
            <button onClick={handleAddCompany} disabled={!newCompanyName || isLoading} className="px-5 py-2.5 font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50">Add Company</button>
          </div>
        </div>
      </Modal>

      {/* Delete Company Modal */}
      <Modal isOpen={deleteCompanyModalOpen} onRequestClose={() => setDeleteCompanyModalOpen(false)} style={modalStyle}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
          <h2 className="text-2xl font-bold mb-4 text-red-600 flex items-center gap-3">
            <div className="bg-red-100 p-2 rounded-full"><FaTrash className="text-red-600" /></div>
            Delete Company
          </h2>
          <p className="mb-6 text-gray-600">
            Are you sure you want to delete <strong className="text-gray-900">{selectedCompany}</strong>? This will permanently remove all associated data.
          </p>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Admin Password</label>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Enter password to confirm"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteCompanyModalOpen(false)} className="px-5 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200">Cancel</button>
            <button onClick={handleDeleteCompany} disabled={!adminPassword || isLoading} className="px-5 py-2.5 font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50">Confirm Delete</button>
          </div>
        </div>
      </Modal>

      {/* Add Division Modal */}
      <Modal isOpen={addDivisionModalOpen} onRequestClose={() => setAddDivisionModalOpen(false)} style={modalStyle}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3">
            <div className="bg-indigo-100 p-2 rounded-full"><FaPlus className="text-indigo-600" /></div>
            Add New Division
          </h2>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Division Name</label>
            <input
              type="text"
              value={newDivisionName}
              onChange={(e) => setNewDivisionName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter division name"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setAddDivisionModalOpen(false)} className="px-5 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200">Cancel</button>
            <button onClick={handleAddDivision} disabled={!newDivisionName || isLoading} className="px-5 py-2.5 font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50">Add Division</button>
          </div>
        </div>
      </Modal>

      {/* Edit Division Modal */}
      <Modal isOpen={editDivisionModalOpen} onRequestClose={() => setEditDivisionModalOpen(false)} style={modalStyle}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3">
            <div className="bg-indigo-100 p-2 rounded-full"><FaEdit className="text-indigo-600" /></div>
            Rename Division
          </h2>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">New Name for {editDivisionOldName}</label>
            <input
              type="text"
              value={editDivisionNewName}
              onChange={(e) => setEditDivisionNewName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter new division name"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setEditDivisionModalOpen(false)} className="px-5 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200">Cancel</button>
            <button onClick={handleEditDivision} disabled={!editDivisionNewName || isLoading} className="px-5 py-2.5 font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50">Save Changes</button>
          </div>
        </div>
      </Modal>

      {/* Delete Division Modal */}
      <Modal isOpen={deleteDivisionModalOpen} onRequestClose={() => setDeleteDivisionModalOpen(false)} style={modalStyle}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
          <h2 className="text-2xl font-bold mb-4 text-red-600 flex items-center gap-3">
            <div className="bg-red-100 p-2 rounded-full"><FaTrash className="text-red-600" /></div>
            Delete Division
          </h2>
          <p className="mb-6 text-gray-600">
            Are you sure you want to delete <strong className="text-gray-900">{divisionToDelete}</strong>? This will permanently remove all associated data.
          </p>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Admin Password</label>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Enter password to confirm"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteDivisionModalOpen(false)} className="px-5 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200">Cancel</button>
            <button onClick={handleDeleteDivision} disabled={!adminPassword || isLoading} className="px-5 py-2.5 font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50">Confirm Delete</button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default Dashboard;